import { useState, useEffect } from "react";
import axios from "axios";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaEye,
  FaMoneyBillWave,
  FaTimesCircle,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-700 border border-yellow-300",
  paid: "bg-green-100 text-green-700 border border-green-300",
  cancelled: "bg-blue-100 text-blue-700 border border-blue-300",
  overdue: "bg-red-100 text-red-700 border border-red-300",
};

const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10,
  });

  const fetchInvoices = async (page = 1, status = "all") => {
    try {
      const token = localStorage.getItem("token");
      const params = { page, per_page: meta.per_page };
      if (status !== "all") params.status = status; // kirim status ke API

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/invoice`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      setInvoices(res.data.data || []);
      setMeta(res.data.meta || {});
    } catch (error) {
      console.error("Error fetching invoices:", error);
      Swal.fire("Error", "Failed to fetch invoices.", "error");
    }
  };

  // Fetch setiap kali currentPage atau filteredStatus berubah
  useEffect(() => {
    fetchInvoices(currentPage, filteredStatus);
  }, [currentPage, filteredStatus]);

  const handleCreate = () => navigate("/inventory/purchase-order");

  const handlePay = async () => {
    if (!evidenceFile) {
      Swal.fire("Warning", "Please upload the payment evidence.", "warning");
      return;
    }
    const formData = new FormData();
    formData.append("files", evidenceFile);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/invoice/pay/${selectedInvoice.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      Swal.fire("Success", "Payment successful!", "success");
      fetchInvoices(currentPage, filteredStatus);
      setIsModalOpen(false);
      setEvidenceFile(null);
    } catch (error) {
      console.error("Error making payment:", error);
      Swal.fire("Error", "Failed to make payment.", "error");
    }
  };

  const handleCancel = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Invoice will be cancelled!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it!",
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/invoice/cancel/${id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire("Cancelled", "Invoice cancelled successfully.", "success");
        fetchInvoices(currentPage, filteredStatus);
      } catch (error) {
        console.error("Error cancelling invoice:", error);
        Swal.fire("Error", "Failed to cancel invoice.", "error");
      }
    }
  };

  const handlePreviousPage = () => {
    if (meta.current_page > 1) setCurrentPage(meta.current_page - 1);
  };
  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) setCurrentPage(meta.current_page + 1);
  };

  return (
    
    <div className="p-6 bg-white to-green-50 min-h-screen rounded-2xl">
     {/* Breadcrumb */}
           <nav className="text-gray-500 text-sm mb-3">
             <ol className="flex items-center gap-2">
               <li className="hover:text-gray-700 transition">Inventory</li>
               <li>/</li>
               <li className="text-gray-700 font-medium">Invoice</li>
             </ol>
           </nav>
     {/* Title */}
     <h2 className="text-3xl font-bold text-gray-800 mb-4 tracking-tight flex items-center gap-2">
             <span className="text-green-600 text-3xl"> 
               <div className="p-3 bg-green-100 rounded-2xl shadow-inner">
               <FaFileInvoiceDollar className="text-green-600" />
               </div>
               </span> Invoice
               
           </h2>
           <p className="text-gray-600">Kelola dan pantau semua invoice dengan mudah</p>
           <br></br>

        {/* FILTER + SEARCH */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            {["all", "pending", "paid", "overdue", "cancelled"].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setFilteredStatus(status);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium shadow-sm transition ${
                  filteredStatus === status
                    ? "bg-green-500 text-white shadow-lg shadow-green-300/50"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search invoice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border px-4 py-2 pr-10 rounded-full w-60 shadow focus:ring-2 focus:ring-green-300"
              />
              <FaSearch className="absolute right-3 top-3 text-gray-400" />
            </div>
            <button className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full shadow">
              <FaFilter className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
        <table className="min-w-full bg-white rounded-2xl">
                <thead className="bg-green-50">
                  <tr>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">#</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">Nomor</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">PO Number</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">Amount</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">Created By</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">Due Date</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600">Status</th>
                <th className="px-6 py-3 text-left font-semibold text-gray-600 text-center">Aksi</th>
              </tr>
            </thead>

            <tbody className="bg-white">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-gray-400 py-8 italic">
                    — Tidak ada data invoice —
                  </td>
                </tr>
              ) : (
                invoices.map((invoice, index) => (
                  <tr key={invoice.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 font-medium">
                      {(meta.current_page - 1) * meta.per_page + index + 1}
                    </td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 font-semibold text-gray-900">{invoice.number}</td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 text-gray-700">{invoice.po_number}</td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 font-bold text-gray-800">
                      Rp {invoice.amount?.toLocaleString("id-ID")}
                    </td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600">{invoice.created_by}</td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 text-gray-600">
                      {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600">
                      <span className={`px-3 py-1 rounded-full font-semibold text-xs ${statusColors[invoice.status]}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-left font-semibold text-gray-600 flex justify-center gap-2">
                      <button
                        onClick={() =>
                          navigate(`/inventory/invoice/detail/${encodeURIComponent(invoice.number)}`)
                        }
                        className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition transform hover:scale-110"
                      >
                        <FaEye className="text-gray-600" />
                      </button>

                      {invoice.status === "pending" && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setIsModalOpen(true);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg shadow transition transform hover:scale-105 flex items-center gap-1 text-sm"
                          >
                            <FaMoneyBillWave /> Pay
                          </button>

                          <button
                            onClick={() => handleCancel(invoice.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg shadow transition transform hover:scale-105 flex items-center gap-1 text-sm"
                          >
                            <FaTimesCircle /> Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Menampilkan{" "}
            <strong>{(meta.current_page - 1) * meta.per_page + 1}</strong> -{" "}
            <strong>{Math.min(meta.current_page * meta.per_page, meta.total)}</strong> dari{" "}
            <strong>{meta.total}</strong> invoice
          </p>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePreviousPage}
              disabled={meta.current_page <= 1}
              className="flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl shadow-sm transition disabled:opacity-50 active:scale-95"
            >
              <FiChevronLeft /> Prev
            </button>

            <span>
              Halaman <strong>{meta.current_page}</strong> dari <strong>{meta.last_page}</strong>
            </span>

            <button
              onClick={handleNextPage}
              disabled={meta.current_page >= meta.last_page}
              className="flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl shadow-sm transition disabled:opacity-50 active:scale-95"
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>

      {/* MODAL PAY */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-[400px]">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Pay Invoice <span className="text-green-600">{selectedInvoice?.number}</span>
            </h3>

            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-green-400 transition"
              onClick={() => document.getElementById("fileUpload").click()}
            >
              <input
                id="fileUpload"
                type="file"
                onChange={(e) => setEvidenceFile(e.target.files[0])}
                className="hidden"
              />
              <svg
                className="w-12 h-12 text-gray-400 mb-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0021 18v-1.5M7.5 12l4.5-4.5 4.5 4.5M12 3v9"
                />
              </svg>
              <p className="text-gray-500 text-sm text-center">
                Drag & drop atau <span className="underline text-green-600">browse</span>
              </p>
              <p className="text-gray-400 text-xs mt-1">Max 10 MB</p>

              {evidenceFile && (
                <p className="mt-3 text-green-600 text-sm font-medium">{evidenceFile.name}</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handlePay}
                className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition"
              >
                Pay
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEvidenceFile(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-xl hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
